import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Grid, Box, Typography, TextField, Button, MenuItem, Select, FormControl, InputLabel,
    CircularProgress, Switch, FormControlLabel, Chip, IconButton, Tooltip as MUITooltip
} from "@mui/material";
import DataTable from "react-data-table-component";
import { DollarSign, Send, RefreshCw, Filter, Clock, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import moment from "moment";
import Swal from "sweetalert2";
import RouteHeader from "../../../components/common/RouteHeader";
import * as PayoutActions from "../../../redux/actions/payoutAction";

const SummaryCard = ({ title, amount, color, icon, subtitle }) => (
    <Box sx={{ background: "#fff", borderRadius: "12px", p: 2.5, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
                <Typography sx={{ fontSize: 13, color: "#64748b", mb: 0.5 }}>{title}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }}>₹{Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</Typography>
                {subtitle && <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3 }}>{subtitle}</Typography>}
            </Box>
            <Box sx={{ width: 44, height: 44, borderRadius: "50%", background: `${color}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {icon || <DollarSign size={22} color={color} />}
            </Box>
        </Box>
    </Box>
);

const statusChip = (status) => {
    const map = {
        Processed: { color: "success", icon: <CheckCircle size={14} /> },
        Processing: { color: "warning", icon: <Clock size={14} /> },
        Pending: { color: "default", icon: <Clock size={14} /> },
        Failed: { color: "error", icon: <XCircle size={14} /> },
        Reversed: { color: "error", icon: <AlertTriangle size={14} /> },
    };
    const config = map[status] || map.Pending;
    return <Chip size="small" label={status} color={config.color} icon={config.icon} variant="outlined" />;
};

const Payouts = () => {
    const dispatch = useDispatch();
    const { payoutSettings, pendingPayouts, payoutHistory, payoutLoading } = useSelector(state => state.payoutReducer);

    // Filters for History
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        dispatch(PayoutActions.getPayoutSettings());
        dispatch(PayoutActions.getPendingPayouts());
        fetchHistory();
    }, [dispatch]);

    const fetchHistory = (page = 1) => {
        setCurrentPage(page);
        dispatch(PayoutActions.getPayoutHistory({
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            recipient_type: filterType || undefined,
            status: filterStatus || undefined,
            page,
            limit: 25,
        }));
    };

    const handleToggleAutoPayout = (e) => {
        const enabled = e.target.checked;
        Swal.fire({
            title: `${enabled ? "Enable" : "Disable"} Auto Payout?`,
            text: enabled ? "Auto payout will automatically process payouts when enabled." : "Auto payout will be disabled.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: enabled ? "Enable" : "Disable",
            confirmButtonColor: enabled ? "#22c55e" : "#ef4444",
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(PayoutActions.toggleAutoPayout({ data: { enabled } }));
            }
        });
    };

    const handleProcessSingle = (recipient) => {
        Swal.fire({
            title: "Process Payout?",
            html: `Transfer <strong>₹${recipient.pending_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong> to <strong>${recipient.name}</strong> (${recipient.recipient_type})?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Process Payout",
            confirmButtonColor: "#6366f1",
        }).then((result) => {
            if (result.isConfirmed) {
                setProcessing(true);
                dispatch(PayoutActions.processPayout({
                    data: { recipient_id: recipient.recipient_id, recipient_type: recipient.recipient_type },
                    onComplete: () => {
                        setProcessing(false);
                        dispatch(PayoutActions.getPendingPayouts());
                        fetchHistory();
                    }
                }));
            }
        });
    };

    const handleProcessAll = () => {
        const total = pendingPayouts?.total_pending || 0;
        Swal.fire({
            title: "Process All Payouts?",
            html: `This will process <strong>₹${total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong> in pending payouts to all recipients.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Process All",
            confirmButtonColor: "#6366f1",
        }).then((result) => {
            if (result.isConfirmed) {
                setProcessing(true);
                dispatch(PayoutActions.processAllPayouts({
                    onComplete: () => {
                        setProcessing(false);
                        dispatch(PayoutActions.getPendingPayouts());
                        fetchHistory();
                    }
                }));
            }
        });
    };

    const allPending = [
        ...(pendingPayouts?.admins || []),
        ...(pendingPayouts?.partners || []),
        ...(pendingPayouts?.affiliates || []),
    ];

    const pendingColumns = [
        { name: "Type", selector: row => row.recipient_type, width: "100px", cell: row => <Chip size="small" label={row.recipient_type} color={row.recipient_type === "Partner" ? "primary" : row.recipient_type === "Affiliate" ? "secondary" : "default"} variant="outlined" /> },
        { name: "Name", selector: row => row.name || "-", sortable: true },
        { name: "Reg. No.", selector: row => row.registration_number || "-", width: "130px" },
        { name: "Pending (₹)", selector: row => Number(row.pending_amount || 0).toFixed(2), sortable: true, right: true, width: "130px" },
        { name: "Records", selector: row => row.record_count, right: true, width: "90px" },
        { name: "Bank", selector: row => row.has_bank_details ? row.bank_name : "Not Available", width: "140px", cell: row => row.has_bank_details ? <Chip size="small" label={row.bank_name || "Added"} color="success" variant="outlined" /> : <Chip size="small" label="Not Available" color="error" variant="outlined" /> },
        {
            name: "Action", width: "120px", cell: row => (
                <Button size="small" variant="contained" disabled={!row.has_bank_details && row.recipient_type !== "Admin" || processing}
                    onClick={() => handleProcessSingle(row)}
                    sx={{ textTransform: "none", fontSize: 12, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", "&:hover": { background: "linear-gradient(135deg, #4f46e5, #7c3aed)" } }}>
                    {processing ? <CircularProgress size={14} color="inherit" /> : <><Send size={12} style={{ marginRight: 4 }} /> Pay</>}
                </Button>
            )
        },
    ];

    const historyColumns = [
        { name: "Date", selector: row => moment(row.createdAt).format("DD MMM YYYY HH:mm"), sortable: true, width: "160px" },
        { name: "Type", selector: row => row.recipient_type, width: "100px", cell: row => <Chip size="small" label={row.recipient_type} color={row.recipient_type === "Partner" ? "primary" : row.recipient_type === "Affiliate" ? "secondary" : "default"} variant="outlined" /> },
        { name: "Recipient", selector: row => row.recipient_name || "-", sortable: true },
        { name: "Reg. No.", selector: row => row.recipient_registration_number || "-", width: "130px" },
        { name: "Amount (₹)", selector: row => Number(row.amount || 0).toFixed(2), sortable: true, right: true, width: "120px" },
        { name: "Bank", selector: row => row.bank_name || "-", width: "130px" },
        { name: "A/C No.", selector: row => row.bank_account_number ? `****${row.bank_account_number.slice(-4)}` : "-", width: "110px" },
        { name: "Status", selector: row => row.status, width: "130px", cell: row => statusChip(row.status) },
        { name: "Razorpay ID", selector: row => row.razorpay_payout_id || "-", width: "170px", cell: row => <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#6366f1" }}>{row.razorpay_payout_id || "-"}</Typography> },
        { name: "Records", selector: row => row.revenue_record_ids?.length || 0, right: true, width: "90px" },
    ];

    const customStyles = {
        headCells: { style: { backgroundColor: "#f8fafc", fontWeight: 600, fontSize: 13, color: "#475569" } },
        cells: { style: { fontSize: 13 } },
    };

    const historySummary = payoutHistory?.summary || {};

    return (
        <Box sx={{ p: 2 }}>
            <RouteHeader title="Payouts" subtitle="Manage and process commission payouts" />

            {/* Auto Payout Toggle + Refresh */}
            <Box sx={{ background: "#fff", borderRadius: "12px", p: 2, mb: 2, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <FormControlLabel
                        control={<Switch checked={payoutSettings?.auto_payout_enabled || false} onChange={handleToggleAutoPayout}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22c55e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#22c55e' } }} />}
                        label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>Auto Payout</Typography>}
                    />
                    <Chip size="small" label={payoutSettings?.auto_payout_enabled ? "Enabled" : "Disabled"} color={payoutSettings?.auto_payout_enabled ? "success" : "default"} />
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <MUITooltip title="Refresh Data">
                        <IconButton onClick={() => { dispatch(PayoutActions.getPendingPayouts()); fetchHistory(); }}>
                            <RefreshCw size={18} />
                        </IconButton>
                    </MUITooltip>
                    {allPending.length > 0 && (
                        <Button variant="contained" onClick={handleProcessAll} disabled={processing}
                            startIcon={processing ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
                            sx={{ textTransform: "none", background: "linear-gradient(135deg, #22c55e, #16a34a)", "&:hover": { background: "linear-gradient(135deg, #16a34a, #15803d)" } }}>
                            Process All (₹{(pendingPayouts?.total_pending || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })})
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard title="Total Pending" amount={pendingPayouts?.total_pending} color="#f59e0b" icon={<Clock size={22} color="#f59e0b" />} subtitle={`${allPending.length} recipients`} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard title="Total Processed" amount={historySummary?.processed_amount} color="#22c55e" icon={<CheckCircle size={22} color="#22c55e" />} subtitle={`${historySummary?.processed_count || 0} payouts`} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard title="Failed" amount={historySummary?.failed_amount} color="#ef4444" icon={<XCircle size={22} color="#ef4444" />} subtitle={`${historySummary?.failed_count || 0} payouts`} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard title="In Processing" amount={historySummary?.pending_amount} color="#6366f1" icon={<RefreshCw size={22} color="#6366f1" />} subtitle={`${historySummary?.pending_count || 0} payouts`} />
                </Grid>
            </Grid>

            {/* Pending Payouts Table */}
            <Box sx={{ background: "#fff", borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden", mb: 2 }}>
                <Box sx={{ p: 2, borderBottom: "1px solid #f1f5f9" }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#1e293b" }}>Pending Payouts</Typography>
                    <Typography sx={{ fontSize: 13, color: "#64748b" }}>Recipients with unpaid commissions</Typography>
                </Box>
                <DataTable
                    columns={pendingColumns}
                    data={allPending}
                    customStyles={customStyles}
                    noDataComponent={<Box sx={{ p: 4, color: "#94a3b8" }}>No pending payouts</Box>}
                    dense
                    progressPending={payoutLoading && !allPending.length}
                />
            </Box>

            {/* Payout History - Filters */}
            <Box sx={{ background: "#fff", borderRadius: "12px", p: 2, mb: 2, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#1e293b", mr: 1 }}>Payout History</Typography>
                <TextField size="small" type="date" label="Start Date" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="End Date" value={endDate} onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={filterType} label="Type" onChange={e => setFilterType(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Admin">Admin</MenuItem>
                        <MenuItem value="Partner">Partner</MenuItem>
                        <MenuItem value="Affiliate">Affiliate</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Processed">Processed</MenuItem>
                        <MenuItem value="Processing">Processing</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Failed">Failed</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={() => fetchHistory(1)} disabled={payoutLoading}
                    startIcon={payoutLoading ? <CircularProgress size={16} color="inherit" /> : <Filter size={16} />}
                    sx={{ textTransform: "none", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", "&:hover": { background: "linear-gradient(135deg, #4f46e5, #7c3aed)" } }}>
                    {payoutLoading ? "Loading..." : "Apply"}
                </Button>
            </Box>

            {/* Payout History Table */}
            <Box sx={{ background: "#fff", borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
                <DataTable
                    columns={historyColumns}
                    data={payoutHistory?.payouts || []}
                    customStyles={customStyles}
                    pagination
                    paginationServer
                    paginationTotalRows={payoutHistory?.total || 0}
                    paginationPerPage={25}
                    paginationDefaultPage={currentPage}
                    onChangePage={page => fetchHistory(page)}
                    noDataComponent={<Box sx={{ p: 4, color: "#94a3b8" }}>No payout history found</Box>}
                    dense
                    progressPending={payoutLoading}
                />
            </Box>
        </Box>
    );
};

export default Payouts;
